import { LogoutMutation } from "@/api-services/auth.services";
import { defaultProfile, chivinoLogo } from "@/assets";
import { useAuth } from "@/hooks/context/useAuth";
import { onClearStorage } from "@/utils/helper/storage";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, Settings } from "lucide-react";
import ChangePasswordModal, { ChangePasswordModalRef } from "./ChangePasswordModal";
import { Button } from "./ui/button";
import PopOver from "./ui/popover";
import { SidebarTrigger } from "./ui/sidebar";
import { SingleBarLoading } from "./ui/skeleton";

type HeaderProps = {
  mobileRightPanelButton?: React.ReactNode;
};

const Header = ({ mobileRightPanelButton }: HeaderProps) => {
  const { userDetails, loading, setUserDetails } = useAuth();
  const [toggleProfile, setToggleProfile] = useState(false);
  const nav = useNavigate();
  const changePasswordRef = useRef<ChangePasswordModalRef>(null);
  const displayName = userDetails?.first_name || userDetails?.artists?.name || "CHIVINO";
  const displayEmail = userDetails?.email || "johndoe@email.com";

  const profileImage = useMemo(() => {
    if (!userDetails) return defaultProfile;

    if (userDetails.user_type === "VENUE") {
      return (userDetails as any)?.venues?.[0]?.profile_image || defaultProfile;
    }

    if (userDetails.user_type === "LABEL") {
      return (userDetails as any)?.labels?.profile_image || defaultProfile;
    }

    return (userDetails as any)?.artists?.profile_image || defaultProfile;
  }, [userDetails]);

  const onSignout = useCallback(async () => {
    try {
      await LogoutMutation();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      onClearStorage();
      setUserDetails(undefined);
      nav("/dashboard", { replace: true });
    }
  }, [nav, setUserDetails]);

  return (
    <div
      className="z-50 flex h-[80px] w-full items-center justify-between gap-3 bg-[#1CACE0] px-3 py-1.5 text-white shadow-[inset_0_-1px_0_rgba(255,255,255,0.18)]"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="md:hidden">
          <SidebarTrigger className="!h-8 !w-8  text-white  [&_svg]:!h-4 [&_svg]:!w-4" />
        </div>
        <div className="hidden min-w-0 items-center gap-3 md:flex">
          <div className="header-logo-container">
            <img src={chivinoLogo} alt="Chivino" className="header-logo" />
          </div>
          <div className="header-user-info">
            <span className="header-user-name">{displayName}</span>
            <span className="header-user-description">The Art of Stone</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {loading ? (
          <SingleBarLoading />
        ) : (
          <>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center  text-white transition "
              aria-label="Settings"
            >
              <Settings className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center  text-white transition "
              aria-label="Messages"
            >
              <MessageSquare className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center  text-white transition "
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
            </button>
            {mobileRightPanelButton}
            <PopOver
              open={toggleProfile}
              onOpenChange={setToggleProfile}
              renderTriggerComponent={
                <div className="flex cursor-pointer items-center gap-2  py-0.5 pr-1 pl-2 transition ">
                  <div className="hidden text-right md:block">
                    <div className="max-w-[150px] truncate text-[12px] leading-tight text-white/85">
                      {displayEmail}
                    </div>
                  </div>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
                    <img src={profileImage} alt="User" className="h-full w-full object-cover" />
                  </div>
                </div>
              }
              renderFooterComponent={
                <div className="w-full px-4 pb-4 flex flex-col items-center gap-2">
                  {/* <Button
                    className="w-full bg-transparent text-[#242424] justify-start"
                    onClick={() => {
                      setShowPinModal(true);
                      setToggleProfile(false);
                    }}
                  >
                    Show PIN
                  </Button>
                  <Button
                    className="w-full bg-transparent text-[#242424] justify-start"
                    onClick={() => {
                      setShowChangePinModal(true);
                      setToggleProfile(false);
                    }}
                  >
                    Change PIN
                  </Button> */}
                  <Button
                    className="w-full bg-transparent text-[#242424] justify-start"
                    onClick={() => {
                      changePasswordRef.current?.open();
                      setToggleProfile(false);
                    }}
                  >
                    Change Password
                  </Button>
                  <Button
                    variant="destructive"
                    className="w-full justify-start"
                    onClick={onSignout}
                  >
                    Logout
                  </Button>
                </div>
              }
            >
              <div className="w-[250px] py-3 px-4 flex flex-col items-center gap-2">
                <p className="text-sm font-semibold text-[#242424] text-center">
                  {userDetails?.user_type === "ARTIST"
                    ? userDetails?.artists?.name || displayName
                    : `${userDetails?.first_name || ""} ${userDetails?.last_name || ""}`.trim() ||
                      displayName}
                </p>
                <p className="text-xs text-[#6B7280]">{displayEmail}</p>
              </div>
            </PopOver>
          </>
        )}
      </div>
      <ChangePasswordModal ref={changePasswordRef} />
    </div>
  );
};

export default Header;
